import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import { isValidSearchDate, getSearchDateParamFromMoment } from 'utils/search/searchDateParam';
import SearchProcessorContainer from './SearchProcessorContainer';

/**
 * Wraps SearchProcessorContainer with the actual date-validity guard for this route — the
 * route itself (`SEARCH_RESULTS_ROUTE_PATH`) doesn't constrain `:departureDate`/`:returnDate`
 * at all, precisely so every date-bearing search-results URL, valid or not, lands here and
 * gets the same treatment instead of some being rejected earlier by a route-level regex.
 * `<Redirect>` replaces history by default, so the invalid URL doesn't linger for the back
 * button.
 * @param {object} routeProps - React Router route props (match, location, history).
 * @returns {JSX.Element} SearchProcessorContainer, or a redirect when dates are invalid.
 */
const ValidatedSearchProcessor = ({ match, location, ...routeProps }) => {
  const { originSlug, destinationSlug, departureDate, returnDate, passengers, way, isOpen } =
    match.params;
  const isDepartureValid = isValidSearchDate(departureDate);
  const datesValid = isDepartureValid && (!returnDate || isValidSearchDate(returnDate));

  if (!datesValid) {
    // Whichever of departure/return is still valid shouldn't be thrown away just because the
    // other is broken — fall back to the one-way form of the same search (today's departure
    // date, or tomorrow's when departure itself was the broken one) instead of sending the
    // user all the way back to origin/destination reselection and losing passengers/leg too.
    // :way and :isOpen are declared as independent optional segments in the route pattern —
    // `/p/:passengers/open` (no `departures`) matches with `way` undefined and `isOpen` set —
    // so each is preserved independently rather than gating `isOpen` on `way` being present.
    // SearchProcessorContainer reads `isOpen` to set `departureIsOpenTicket`; dropping it here
    // would silently turn an open-ticket departure into a fixed-date one.
    const suffix = `${way ? `/${way}` : ''}${isOpen ? `/${isOpen}` : ''}`;
    const fallbackDepartureDate = isDepartureValid
      ? departureDate
      : getSearchDateParamFromMoment(moment().add(1, 'days'));
    const fallbackPathname = `/search/${originSlug}/${destinationSlug}/${fallbackDepartureDate}/p/${passengers}${suffix}`;
    // Preserve query params (coupons, utm_*, analytics_*, ?isExchange=true) — only the
    // date-bearing path segments are being discarded here, not the whole navigation intent.
    return <Redirect to={{ pathname: fallbackPathname, search: location.search }} />;
  }

  return <SearchProcessorContainer match={match} location={location} {...routeProps} />;
};

ValidatedSearchProcessor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      originSlug: PropTypes.string,
      destinationSlug: PropTypes.string,
      departureDate: PropTypes.string,
      returnDate: PropTypes.string,
      passengers: PropTypes.string,
      way: PropTypes.string,
      isOpen: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default ValidatedSearchProcessor;
